/**
 * Breadcrumb trail context — lets a page append a trailing crumb (e.g. the
 * name of the entity being viewed) to the shell breadcrumb.
 *
 * The shell renders `Workspace › Section`; a detail page calls
 * `usePageBreadcrumb([{ label: kb.name }])` to extend it to
 * `Workspace › Section › Another test`. Since the page title now lives in
 * the breadcrumb (no in-page `<h1>`), this keeps deep pages self-locating.
 */

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router";

export interface TrailCrumb {
  label: string;
  /** Optional link target; the last crumb is rendered as the current page. */
  to?: string;
}

interface TrailContextValue {
  trail: TrailCrumb[];
  /**
   * Write the trail, tagged with the originating pathname. The provider drops
   * a write (set or clear) whose pathname no longer matches the current
   * location, so a stale page's unmount cleanup can't clobber the incoming
   * page's crumb across the lazy/Suspense boundary.
   */
  setTrail: (crumbs: TrailCrumb[], pathname: string) => void;
}

const BreadcrumbTrailContext = createContext<TrailContextValue | null>(null);

export function BreadcrumbTrailProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<TrailCrumb[]>([]);
  const location = useLocation();
  // Track the live pathname in a ref so the stable `setTrail` identity reads
  // the current value without becoming a render-time dependency.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const guardedSetTrail = useRef<TrailContextValue["setTrail"]>((crumbs, pathname) => {
    if (pathname !== pathnameRef.current) return;
    setTrail(crumbs);
  }).current;

  return (
    <BreadcrumbTrailContext.Provider value={{ trail, setTrail: guardedSetTrail }}>
      {children}
    </BreadcrumbTrailContext.Provider>
  );
}

export function useBreadcrumbTrail(): TrailCrumb[] {
  return useContext(BreadcrumbTrailContext)?.trail ?? [];
}

/**
 * Register trailing crumbs for the current page. Cleared automatically when
 * the page unmounts. Pass a stable array (e.g. derived from loaded data).
 */
export function usePageBreadcrumb(crumbs: TrailCrumb[]): void {
  const ctx = useContext(BreadcrumbTrailContext);
  const { pathname } = useLocation();
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    ctx?.setTrail(crumbs, pathname);
    // Tag the cleanup with the pathname captured at mount; the provider skips
    // it if the location has already moved on to a newer page.
    return () => ctx?.setTrail([], pathname);
    // `key` captures the crumbs' contents; `ctx` is stable from the provider.
  }, [key, pathname]);
}
